import React from "react";
import { motion } from "framer-motion";
import ViewWrapper from "./ViewWrapper";
import "../../styles/Views/about.scss";

const skills = [
  {
    category: "Languages",
    items: [
      "JavaScript (proficient)",
      "Python (proficient)",
      "C (proficient)",
      "Java\\C# (working knowledge)",
      "Bash scripting (working knowledge)",
      "x86 Assembly",
    ],
  },
  {
    category: "Web & Frontend",
    items: [
      "React",
      "Next.js",
      "Node.js",
      "Express.js",
      "REST APIs",
      "HTML",
      "CSS",
    ],
  },
  {
    category: "Data & Databases",
    items: [
      "Firebase",
      "MongoDB",
    ],
  },
  {
    category: "Networking",
    items: [
      "TCP/IP",
      "OSI Model",
      "IPv4/IPv6",
      "DHCP",
      "NAT",
      "DNS",
      "HTTP/SSH",
      "IP Routing",
    ],
  },
  {
    category: "Operating Systems",
    items: [
      "Linux administration",
      "File system management",
      "Process management",
      "Service installation",
      "Virtualization",
    ],
  },
  {
    category: "Security & Systems",
    items: [
      "Linux kernel development",
      "Network security",
      "IPS/DLP",
      "Netfilter",
    ],
  },
  {
    category: "Cybersecurity — Offensive",
    items: [
      "Penetration testing",
      "OSINT & DNS enumeration",
      "Nmap",
      "Vulnerability analysis",
      "Metasploit",
      "Brute force attacks",
      "Burp Suite",
      "Wireshark",
    ],
  },
  {
    category: "Low-Level & Exploitation",
    items: [
      "IDA",
      "GDB",
      "Buffer Overflow",
      "Return-Oriented Programming (ROP)",
      "Return-to-libc",
      "Reverse engineering",
    ],
  },
  {
    category: "Tools & Methods",
    items: ["Git", "OOP", "Nmap", "Metasploit", "Burp Suite", "Wireshark"],
  },
];
const highlights = [
  {
    icon: "🏗️",
    title: "Founded a Live SaaS Platform",
    body: "Architected Dukkana — a production multi-tenant e-commerce engine (Next.js / Express.js), with PayPal payment integration and a custom CI/CD pipeline.",
  },
  {
    icon: "🔒",
    title: "Linux Kernel Security Engineer",
    body: "Built a stateful firewall as a kernel module using Netfilter hooks with built-in IPS, DLP, and MITM proxy support — including CVE-specific rules for two Apache OFBiz vulnerabilities.",
  },
  {
    icon: "🎓",
    title: "Academic Research",
    body: "Co-author of an upcoming  peer-reviewed scientific publication at Prof. Dominique Lamy's Lab (TAU). Applied data engineering, machine learning tools, and mathematical modeling to analyze complex visual data",
  },
  {
    icon: "📐",
    title: "Educator & Mentor",
    body: "Designed and lectured full Linear Algebra courses at Tel Aviv University for three consecutive academic years, reaching hundreds of students.",
  },
];

const About = () => {
  return (
    <ViewWrapper>
      <div className="about-page">
        {/* Header */}
        <motion.div
          className="about-header"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          <span className="about-eyebrow">About</span>
          <h1 className="about-heading">
            Engineer. Founder.{" "}
            <span className="about-heading--accent">Security Specialist.</span>
          </h1>
          <p className="about-intro">
            I'm Tamer Razaq — a full-stack engineer and cybersecurity specialist
            with proven end-to-end product ownership. I combine systems-level
            engineering (Linux kernel development), offensive/defensive security
            expertise (Technion-certified), and a rigorous academic background
            (B.Sc. Computer Science & Psychology, TAU).
          </p>
          <p className="about-intro">
            I build things that ship to production and stay secure in the wild.
            I'm available for <strong>remote contracts</strong> and{" "}
            <strong>full-time roles</strong> globally.
          </p>
          <div className="about-contact-row">
            <a
              href="https://www.linkedin.com/in/tamer-a-532931196/"
              target="_blank"
              rel="noreferrer"
              className="about-link"
            >
              LinkedIn ↗
            </a>
            <a
              href="https://github.com/Tamerabdalrazaq"
              target="_blank"
              rel="noreferrer"
              className="about-link"
            >
              GitHub ↗
            </a>
          </div>
        </motion.div>

        {/* Highlights grid */}
        <motion.div
          className="about-highlights"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7, delay: 0.25 }}
        >
          {highlights.map((h, i) => (
            <motion.div
              key={h.title}
              className="about-highlight-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.15 * i + 0.3 }}
            >
              <span className="about-highlight-icon">{h.icon}</span>
              <h3 className="about-highlight-title">{h.title}</h3>
              <p className="about-highlight-body">{h.body}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* Skills */}
        <motion.div
          className="about-skills-section"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.5 }}
        >
          <h2 className="about-section-title">Technical Skills</h2>
          <div className="about-skills-grid">
            {skills.map((s) => (
              <div className="about-skill-group" key={s.category}>
                <h4 className="about-skill-category">{s.category}</h4>
                <div className="about-skill-tags">
                  {s.items.map((item) => (
                    <span className="about-tag" key={item}>
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Languages spoken */}
        <motion.div
          className="about-languages"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.7 }}
        >
          <span className="about-lang-label">Languages spoken:</span>
          {[
            "Arabic (native)",
            "Hebrew (excellent)",
            "English (excellent)",
            "Spanish (basic)",
          ].map((l) => (
            <span className="about-tag about-tag--lang" key={l}>
              {l}
            </span>
          ))}
        </motion.div>
      </div>
    </ViewWrapper>
  );
};

export default About;
